import { useEffect, useState } from "react";
import { Search, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { sub, formatISO, type Duration } from "date-fns";

interface SearchBarProps {
  onSearch: (query: string, timeStart: string, timeEnd: string) => void;
  isSubmitting: boolean;
  initialQuery?: string;
}

type TimeRange = "1h" | "4h" | "24h" | "7d" | "30d" | "custom";

export function SearchBar({ onSearch, isSubmitting, initialQuery = "" }: SearchBarProps) {
  const [query, setQuery] = useState(initialQuery);
  const [timeRange, setTimeRange] = useState<TimeRange>("24h");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setValidationError(null);

    const now = new Date();
    let timeStart: string;
    let timeEnd: string;

    if (timeRange === "custom") {
      if (!customStart || !customEnd) {
        setValidationError("Choose both a start and end time.");
        return;
      }
      const start = new Date(customStart);
      const end = new Date(customEnd);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        setValidationError("Enter a valid start and end time.");
        return;
      }
      if (start >= end) {
        setValidationError("Start time must be before end time.");
        return;
      }
      if (end.getTime() - start.getTime() > 30 * 24 * 60 * 60 * 1000) {
        setValidationError("Custom ranges cannot exceed 30 days.");
        return;
      }
      timeStart = formatISO(start);
      timeEnd = formatISO(end);
    } else {
      timeEnd = formatISO(now);
      const durations: Record<string, Duration> = {
        "1h": { hours: 1 },
        "4h": { hours: 4 },
        "24h": { hours: 24 },
        "7d": { days: 7 },
        "30d": { hours: 720 },
      };
      timeStart = formatISO(sub(now, durations[timeRange]));
    }

    onSearch(query, timeStart, timeEnd);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Investigation question"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. failed logins from 203.0.113.10 yesterday"
            className="pl-9"
            maxLength={500}
            required
          />
        </div>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Search className="mr-1.5 h-4 w-4" />
          )}
          Find events
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Describe the events you want to find. Use Logs for an exact message search.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="investigation-time-range" className="text-xs text-muted-foreground">Time Range</Label>
          <select
            id="investigation-time-range"
            value={timeRange}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTimeRange(e.target.value as TimeRange)}
            className="mt-1 flex h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="1h">Last 1 hour</option>
            <option value="4h">Last 4 hours</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Custom range</option>
          </select>
        </div>

        {timeRange === "custom" && (
          <>
            <div>
              <Label htmlFor="investigation-custom-start" className="text-xs text-muted-foreground">Start</Label>
              <Input
                id="investigation-custom-start"
                type="datetime-local"
                value={customStart}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomStart(e.target.value)}
                className="mt-1 h-9"
              />
            </div>
            <div>
              <Label htmlFor="investigation-custom-end" className="text-xs text-muted-foreground">End</Label>
              <Input
                id="investigation-custom-end"
                type="datetime-local"
                value={customEnd}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomEnd(e.target.value)}
                className="mt-1 h-9"
              />
            </div>
          </>
        )}
      </div>

      {validationError && (
        <p role="alert" className="flex items-center gap-1.5 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          {validationError}
        </p>
      )}
    </form>
  );
}
